// src/components/Qbus/BulkFriendlyNameDialog.jsx
// Dialog for bulk assigning friendly names to Qbus outputs based on naming conventions
import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  AutoFixHigh as AutoIcon,
  Search as SearchIcon,
  CheckCircle as DoneIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { qbusApi } from '../../services/apiClient';

// ─── Auto-suggestion rules ───────────────────────────────────────
const PREFIX_MAP = [
  { prefix: 'VERL_', replacement: 'Verlichting ' },
  { prefix: 'R_', replacement: 'Relais ' },
  { prefix: 'I_MC_', replacement: 'Contact ' },
  { prefix: 'F_', replacement: '' },
  { prefix: 'BEW_', replacement: 'Beweging ' },
  { prefix: 'tsd_', replacement: 'TSD ' },
];

const ROOM_MAP = {
  'SLPK': 'Slaapkamer',
  'BADK': 'Badkamer',
  'BADKAMER': 'Badkamer',
  'WC': 'WC',
  'INKOM': 'Inkomhal',
  'BERGING': 'Berging',
  'KEUKEN': 'Keuken',
  'WOONKAMER': 'Woonkamer',
  'EETKAMER': 'Eetkamer',
  'TERRAS': 'Terras',
  'NACHTGANG': 'Nachtgang',
  'VOORDEUR': 'Voordeur',
  'SPIEGEL': 'Spiegel',
  'PANIEK': 'Paniek',
  'RAAM': 'Raam',
};

const WORD_MAP = {
  'SLOT': 'Slot',
  'BEL': 'Bel',
  'KAST': 'Kast',
  'DELAY': 'Delay',
  'RESET': 'Reset',
  'SET': 'Set',
  'REQUEST': 'Request',
  'LANG': 'Lang',
  'OPEN': 'Open',
  'RESERVE': 'Reserve',
  'RESERVER': 'Reserve',
  'STOPCONTACT': 'Stopcontact',
  'KEUKENBLOK': 'Keukenblok',
};

const FULL_NAME_MAP = {
  'ALLES UIT': 'Alles Uit',
  'WEG': 'Weg',
  'GAAN SLAPEN SLPK 1': 'Gaan Slapen Slaapkamer 1',
  'Simulatie': 'Simulatie',
  'Sim. Opnemen': 'Simulatie Opnemen',
};

function suggestFriendlyName(originalName) {
  if (!originalName) return '';

  // Check full-name overrides first
  if (FULL_NAME_MAP[originalName]) return FULL_NAME_MAP[originalName];

  let name = originalName;

  // Strip known prefixes
  for (const { prefix, replacement } of PREFIX_MAP) {
    if (name.startsWith(prefix)) {
      name = replacement + name.slice(prefix.length);
      break;
    }
  }

  // Replace underscores with spaces
  name = name.replace(/_/g, ' ');

  // Expand known room/word abbreviations
  const words = name.split(/\s+/);
  const expanded = words.map(word => {
    const upper = word.toUpperCase();
    // Check room map
    if (ROOM_MAP[upper]) return ROOM_MAP[upper];
    // Check word map
    if (WORD_MAP[upper]) return WORD_MAP[upper];
    // Title case if all-uppercase, preserve if mixed
    if (word === word.toUpperCase() && word.length > 1) {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }
    return word;
  });

  return expanded.join(' ').trim();
}

// ─── Component ───────────────────────────────────────────────────
const BulkFriendlyNameDialog = ({ open, onClose, locationId, onApplied }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uniqueNames, setUniqueNames] = useState([]);
  const [friendlyNames, setFriendlyNames] = useState({});
  const [search, setSearch] = useState('');
  const [onlyEmpty, setOnlyEmpty] = useState(true);

  // Load unique names when dialog opens
  useEffect(() => {
    if (!open || !locationId) return;
    setLoading(true);
    qbusApi.getUniqueOutputNames(locationId)
      .then((res) => {
        const names = res.data.uniqueNames || [];
        setUniqueNames(names);

        // Pre-fill with existing or suggested friendly names
        const initial = {};
        names.forEach(n => {
          initial[n.name] = n.existingFriendlyName || suggestFriendlyName(n.name);
        });
        setFriendlyNames(initial);
      })
      .catch((err) => {
        console.error('Error loading unique names:', err);
      })
      .finally(() => setLoading(false));
  }, [open, locationId]);

  // Filter names
  const filteredNames = useMemo(() => {
    let filtered = uniqueNames;
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(n =>
        n.name?.toLowerCase().includes(q) ||
        friendlyNames[n.name]?.toLowerCase().includes(q)
      );
    }
    if (onlyEmpty) {
      filtered = filtered.filter(n => !n.existingFriendlyName);
    }
    return filtered;
  }, [uniqueNames, search, onlyEmpty, friendlyNames]);

  // Stats
  const totalCount = uniqueNames.length;
  const withFriendly = uniqueNames.filter(n => n.existingFriendlyName).length;
  const willUpdate = filteredNames.filter(n =>
    friendlyNames[n.name] && friendlyNames[n.name] !== n.existingFriendlyName
  ).length;

  // Re-suggest all
  const handleAutoSuggestAll = () => {
    const updated = { ...friendlyNames };
    filteredNames.forEach(n => {
      if (!n.existingFriendlyName) {
        updated[n.name] = suggestFriendlyName(n.name);
      }
    });
    setFriendlyNames(updated);
  };

  // Apply
  const handleApply = async () => {
    const mappings = filteredNames
      .filter(n => friendlyNames[n.name] && friendlyNames[n.name] !== n.existingFriendlyName)
      .map(n => ({ name: n.name, friendlyName: friendlyNames[n.name] }));

    if (mappings.length === 0) return;

    setSaving(true);
    try {
      const result = await qbusApi.bulkUpdateFriendlyNames(locationId, mappings);
      onApplied?.(result.data);
      onClose();
    } catch (err) {
      console.error('Error applying friendly names:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon />
          <Typography variant="h6">Bulk Assign Friendly Names</Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip label={`${totalCount} unique names`} size="small" variant="outlined" />
          <Chip
            label={`${withFriendly} already named`}
            size="small"
            color="success"
            variant="outlined"
            icon={<DoneIcon />}
          />
          <Chip
            label={`${willUpdate} to update`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search names..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={onlyEmpty}
                onChange={(e) => setOnlyEmpty(e.target.checked)}
                size="small"
              />
            }
            label={<Typography variant="body2">Only unnamed</Typography>}
          />
          <Tooltip title="Re-generate suggestions for all unnamed outputs">
            <Button
              size="small"
              startIcon={<AutoIcon />}
              onClick={handleAutoSuggestAll}
            >
              Auto-suggest
            </Button>
          </Tooltip>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredNames.length === 0 ? (
          <Alert severity="info">
            {onlyEmpty && withFriendly === totalCount
              ? 'All outputs already have friendly names. Uncheck "Only unnamed" to review them.'
              : 'No matching output names found.'}
          </Alert>
        ) : (
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: '35%' }}>Original Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '10%' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '8%' }} align="center">Count</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '47%' }}>Friendly Name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredNames.map((entry) => {
                  const isChanged = friendlyNames[entry.name] && friendlyNames[entry.name] !== entry.existingFriendlyName;
                  return (
                    <TableRow key={entry.name} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {entry.name || <em>empty</em>}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={entry.functionBlockType || '?'}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={entry.count} size="small" />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={friendlyNames[entry.name] || ''}
                          onChange={(e) => setFriendlyNames(prev => ({
                            ...prev,
                            [entry.name]: e.target.value
                          }))}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: isChanged ? 'action.selected' : 'transparent',
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={saving || willUpdate === 0}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {saving ? 'Applying...' : `Apply ${willUpdate} name${willUpdate !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkFriendlyNameDialog;
